import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import axios from '../services/request';

// ISP-9999 (dev port of FE#6343): kept out of config.js's API_PATH map on
// purpose -- firebase.js must not import config.js, which pulls in every
// asset and breaks this file's unit tests (see firebase.unsupported.test.js).
const FCM_DEVICES_PATH = '/api/v1/devices/';

// Firebase configuration object
const firebaseConfig = {
  apiKey: 'AIzaSyBC4qHc6MMPoEFTcHllor26yrWA3CVjS14',
  authDomain: 'iyuga-wheel.firebaseapp.com',
  projectId: 'iyuga-wheel',
  storageBucket: 'iyuga-wheel.firebasestorage.app',
  messagingSenderId: '594528857490',
  appId: '1:594528857490:web:fda8eaace9c5aabc7b254b',
  measurementId: 'G-L77XRTWECD',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize messaging conditionally based on browser support
let messaging = null;

// Function to initialize messaging safely
const initializeMessaging = async () => {
  try {
    // Check if Firebase messaging is supported in this browser
    const supported = await isSupported();
    if (supported) {
      messaging = getMessaging(app);
      console.log('Firebase messaging initialized successfully');
    } else {
      console.log('Firebase messaging not supported in this browser');
    }
  } catch (error) {
    console.warn('Failed to initialize Firebase messaging:', error);
  }
};

// Initialize messaging immediately. Callers that read `messaging` synchronously
// right after import (NotificationHandler's mount effect, EnableNotificationsBanner's
// click handler via requestNotificationPermission below) can otherwise run before
// this async check settles and silently no-op with no retry — await this promise
// before checking `messaging` anywhere in this module.
const messagingReadyPromise = initializeMessaging();

function readAuthToken() {
  try {
    const stored = JSON.parse(localStorage.getItem('keyCloak') || '{}');
    return stored?.token || null;
  } catch {
    return null;
  }
}

// Dedupe POST /devices/ for the same (auth token, FCM token) pair this session.
let lastRegisteredKey = null;

export function resetFcmDeviceRegisterState() {
  lastRegisteredKey = null;
}

/**
 * ISP-9999 (dev port of FE#6343): bind this browser's FCM token to the
 * logged-in user so Backend can push -- a repo-wide grep for
 * `POST /api/v1/devices/` previously returned 0 hits (firebase.deviceRegistration.test.js),
 * i.e. no client ever registered a token, so the FCMDevice row backing the
 * in-app notification feed was never created. Skips when logged out (login
 * itself persists device_token when the login POST includes one).
 */
export async function registerFcmDevice(token) {
  if (!token) return null;
  const authToken = readAuthToken();
  if (!authToken) {
    console.log('FIREBASE == skip device register, not logged in');
    return null;
  }
  const registerKey = `${authToken}:${token}`;
  if (lastRegisteredKey === registerKey) {
    return true;
  }
  try {
    const res = await axios.post(FCM_DEVICES_PATH, {
      registration_id: token,
      type: 'web',
    });
    lastRegisteredKey = registerKey;
    console.log('FIREBASE == device registered with backend:', res?.data);
    return res?.data ?? true;
  } catch (error) {
    console.warn('FIREBASE == device register failed:', error?.response?.data || error);
    return null;
  }
}

/**
 * Request notification permission and retrieve FCM token.
 */
const requestNotificationPermission = async () => {
  try {
    await messagingReadyPromise;

    // Check if messaging was successfully initialized
    if (!messaging) {
      console.log('Firebase messaging not available');
      return null;
    }

    // Check if Notification API is available
    if (!('Notification' in window)) {
      console.log('Notification API not supported');
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      const token = await getToken(messaging, {
        vapidKey:
          'BF3nTK8u9-IdI5qVbDa1kwd3pQTdVLseFYtB2ciCVXWj7pm80IHM7gTopi6rh3scvd9pSOkotER0f-Vsf6jqb2I',
      });
      console.log('FIREBASE == FCM Token:', token); // Send this token to your backend
      if (token) await registerFcmDevice(token);
      return token;
    } else {
      console.log('FIREBASE == Notification permission denied.');
      return null;
    }
  } catch (error) {
    console.warn('FIREBASE == Error requesting notification permission:', error);
    return null;
  }
};

/**
 * Listen for foreground push notifications.
 * @param {Function} callback - Function to handle incoming messages.
 */
const onMessageListener = async (callback) => {
  await messagingReadyPromise;

  if (!messaging) {
    console.log('Firebase messaging not available, skipping message listener');
    return;
  }

  try {
    onMessage(messaging, (payload) => {
      console.log('FIREBASE == Foreground notification received:', payload);
      if (callback) callback(payload);
    });
  } catch (error) {
    console.warn('Error setting up message listener:', error);
  }
};

export { messaging, requestNotificationPermission, onMessageListener, messagingReadyPromise };

// const PushNotification = () => {
//     useEffect(() => {
//       requestNotificationPermission();

//       // Handle incoming messages when the app is in the foreground
//       onMessage(messaging, (payload) => {
//         console.log("Message received:", payload);
//         alert(`Notification: ${payload.notification.title}`);
//       });
//     }, []);

//     return <div>Firebase Push Notifications Setup</div>;
//   };

//   export default PushNotification;
